import { FC, ReactNode, useCallback, useContext, useEffect } from 'react';

import {
  IconAlertTriangle,
  IconLock,
  IconLogin,
  IconShieldLock,
  IconWifiOff,
} from '@tabler/icons-react';
import config from 'config';
import { AuthContext, AuthContextAction } from 'context/AuthContext';
import { Badge, Button, SpinnerComponent } from 'ui-kit';
import { performLogout, useUsername } from 'utils/authUtils';

import UserAuthentication from '../UserAuthentication';

import type { FailureDescription } from './core';
import { DocumentViewer } from './DocumentViewer';
import { viewerConfig } from './irmConfig';
import { PiiNoticeModal } from './PiiNoticeModal';
import { StatusCard } from './StatusCard';
import { useViewerSession } from './useViewerSession';

import './irmViewer.css';

const EXPECTATIONS = [
  'Your access is checked every time you open this document.',
  'Your name, the time and your IP address are shown across every page.',
  'The document closes by itself when your access ends.',
];

const Shell: FC<{ children: ReactNode; bare?: boolean }> = ({ children, bare }) => (
  <div className="irm-viewer-root flex h-full min-h-screen flex-col bg-background-canvas text-white light:bg-gray-100 light:text-gray-900">
    {!bare && (
      <header className="flex items-center gap-2 border-b border-slate-500 px-4 py-3 text-sm text-gray-300">
        <IconShieldLock size={20} className="text-purple-300" aria-hidden="true" />
        ORSNN protected documents
      </header>
    )}
    {children}
    <p className="irm-print-notice">Printing is disabled for protected documents.</p>
  </div>
);

const closeWindow = () => window.close();

const SignedInAs: FC = () => {
  const username = useUsername();
  return username ? <p className="mt-4 text-xs text-gray-400">Signed in as {username}</p> : null;
};

export const IrmViewerRoute: FC<{ isUserSignedIn: boolean }> = ({ isUserSignedIn }) => {
  const { state, open, acceptNotice, declineNotice, closeRefusedNotice, close, backToReady } =
    useViewerSession(viewerConfig);
  const { dispatch } = useContext(AuthContext);
  const sessionActive =
    state.name === 'open' || state.name === 'opening' || state.name === 'notice';

  useEffect(() => {
    if (!isUserSignedIn && sessionActive) {
      backToReady();
    }
  }, [isUserSignedIn, sessionActive, backToReady]);

  const signInAgain = useCallback(async () => {
    const logoutUrl = await performLogout(config.website.URL);
    backToReady();
    dispatch({ type: AuthContextAction.SIGN_OUT });
    if (logoutUrl) {
      window.location.href = logoutUrl;
    }
  }, [backToReady, dispatch]);

  const closeAction = window.opener ? (
    <Button label="Close this window" type="secondary" onClick={closeWindow}>
      Close this window
    </Button>
  ) : undefined;

  if (state.name === 'unconfigured') {
    return (
      <Shell>
        <StatusCard
          tone="warning"
          icon={<IconAlertTriangle size={28} />}
          title="Protected documents are not available here"
          announce="alert"
        >
          This environment isn’t set up to open protected documents.
        </StatusCard>
      </Shell>
    );
  }

  if (state.name === 'unusable') {
    return (
      <Shell>
        <StatusCard
          tone="error"
          icon={<IconAlertTriangle size={28} />}
          title={state.failure.title}
          announce="alert"
          actions={closeAction}
        >
          {state.failure.body}
        </StatusCard>
      </Shell>
    );
  }

  if (!isUserSignedIn) {
    return <UserAuthentication />;
  }

  const stopped = (failure: FailureDescription) => {
    if (failure.next === 'sign-in') {
      return (
        <StatusCard
          tone="info"
          icon={<IconLogin size={28} />}
          title={failure.title}
          announce="alert"
          actions={
            <Button label="Sign in again" onClick={signInAgain}>
              Sign in again
            </Button>
          }
        >
          <p>{failure.body}</p>
          <p className="mt-2 text-xs text-gray-400">
            If this window doesn’t come back to your document after you sign in, open the protected
            file from your downloads again.
          </p>
        </StatusCard>
      );
    }
    if (failure.next === 'retry') {
      return (
        <StatusCard
          tone="warning"
          icon={<IconWifiOff size={28} />}
          title={failure.title}
          announce="alert"
          actions={
            <Button label="Open again" onClick={() => open()}>
              Open again
            </Button>
          }
        >
          {failure.body}
        </StatusCard>
      );
    }
    return (
      <StatusCard
        tone="error"
        icon={<IconLock size={28} />}
        title={failure.title}
        announce="alert"
        actions={closeAction}
      >
        {failure.body}
      </StatusCard>
    );
  };

  switch (state.name) {
    case 'receiving':
      return (
        <Shell>
          <StatusCard
            tone="info"
            icon={<IconShieldLock size={28} />}
            title="Receiving your document"
          >
            <div className="flex flex-col items-center gap-3">
              <SpinnerComponent loading positionRelative />
              <p>Waiting for the file you opened to hand over your document…</p>
            </div>
          </StatusCard>
        </Shell>
      );
    case 'ready':
      return (
        <Shell>
          <StatusCard
            tone="success"
            icon={<IconShieldLock size={28} />}
            title="Your protected document is ready"
            actions={
              <Button label="Open document" onClick={() => open()}>
                Open document
              </Button>
            }
          >
            <div className="flex items-center justify-center gap-2">
              <Badge label={`Sale ${state.saleKey}`}>Sale {state.saleKey}</Badge>
            </div>
            <ul className="mx-auto mt-4 max-w-md list-disc space-y-1 pl-5 text-left">
              {EXPECTATIONS.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
            <SignedInAs />
          </StatusCard>
        </Shell>
      );
    case 'opening':
    case 'notice':
      return (
        <Shell>
          <StatusCard tone="info" icon={<IconShieldLock size={28} />} title="Opening your document">
            <div className="flex flex-col items-center gap-3">
              <SpinnerComponent loading positionRelative />
              <p>Confirming your access and unlocking the file. This only takes a moment.</p>
            </div>
          </StatusCard>
          {state.name === 'notice' && (
            <PiiNoticeModal
              onAccept={acceptNotice}
              onDecline={declineNotice}
              onRefusalClosed={closeRefusedNotice}
            />
          )}
        </Shell>
      );
    case 'open':
      return (
        <Shell bare>
          <DocumentViewer
            document={state.document}
            saleKey={state.saleKey}
            suspended={state.suspended}
            onClose={close}
          />
        </Shell>
      );
    case 'stopped':
      return <Shell>{stopped(state.failure)}</Shell>;
    default:
      return null;
  }
};
